import React, { useContext, useEffect, useState } from 'react';
import { GlobalContext } from '../context/GlobalContext';
import useApi from '../hooks/useApi';
import { Loader2, Calendar } from 'lucide-react';
import { format } from 'date-fns';

// Report Components - Import from Advanced Reports (backward compatibility)
import InvoiceSummary from '../components/AdvancedReports/reports/InvoiceSummary';
import SixMonthSales from '../components/AdvancedReports/reports/SixMonthSales';
import AreaAnalysis from '../components/AdvancedReports/reports/AreaAnalysis';
import DailySales from '../components/AdvancedReports/reports/DailySales';
import WeeklySales from '../components/AdvancedReports/reports/WeeklySales';
import QuotesReport from '../components/AdvancedReports/reports/QuotesReport';

function Sales() {
  const { operatorData, apir } = useContext(GlobalContext);
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const currentWeek = getISOWeek();

  const [filters, setFilters] = useState({
    reportType: 'invoice-summary',
    month: currentMonth,
    year: currentYear,
    week: currentWeek,
    rep: '',
    branch: '',
    searchTerm: '',
    period: 'today',
    startDate: format(new Date(), 'yyyyMMdd'),
    endDate: format(new Date(), 'yyyyMMdd')
  });

  // Helper function to get ISO week number
  function getISOWeek() {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + 3 - (date.getDay() + 6) % 7);
    const week1 = new Date(date.getFullYear(), 0, 4);
    return 1 + Math.round(((date.getTime() - week1.getTime()) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
  }

  const { data: branches } = useApi({
    url: '/factor/cplusassist/branches',
    initial: [],
    enabled: !!apir
  });

  const { data: operators } = useApi({
    url: '/salesassist/cplusassist/getOperators',
    initial: [],
    enabled: !!apir && filters.reportType === 'weekly-sales'
  });

  const {
    data: salesData,
    isLoading,
    isFetching,
    refetch: fetchSalesData
  } = useApi({
    url: filters.reportType === 'invoice-summary'
      ? `/visiondashboard/salesassist/getSales/${filters.year}/${filters.month}/${filters.branch}`
      : filters.reportType === 'six-month-sales'
        ? `/visiondashboard/salesassist/getSixMonthSales/${filters.branch}`
        : filters.reportType === 'area-analysis'
          ? `/visiondashboard/salesassist/getAreaAnalysis/${filters.period}/${filters.branch}`
          : filters.reportType === 'weekly-sales'
            ? `/visiondashboard/salesassist/getWeeklySales/${filters.week}${filters.rep && filters.rep !== '' ? `/${filters.rep}` : ''}`
            : `/visiondashboard/salesassist/getDailySales/${filters.startDate.replace(/-/g, '')}/${filters.endDate.replace(/-/g, '')}/${filters.branch || ''}`,
    initial: [],
    enabled: false
  });

  const handleFetch = () => {
    fetchSalesData();
  };

  useEffect(() => {
    setFilters(prev => ({ ...prev }));
  }, [filters.reportType]);

  const renderFilters = () => {
    switch (filters.reportType) {
      case 'invoice-summary':
        return (
          <>
            <select
              value={filters.month}
              onChange={(e) => setFilters(prev => ({ ...prev, month: parseInt(e.target.value) }))}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              disabled={isFetching}
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                <option key={month} value={month}>
                  {new Date(2024, month - 1).toLocaleString('default', { month: 'long' })}
                </option>
              ))}
            </select>

            <select
              value={filters.year}
              onChange={(e) => setFilters(prev => ({ ...prev, year: parseInt(e.target.value) }))}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              disabled={isFetching}
            >
              {Array.from({ length: 4 }, (_, i) => currentYear - i).map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </>
        );

      case 'weekly-sales':
        return (
          <>
            <select
              value={filters.week}
              onChange={(e) => setFilters(prev => ({ ...prev, week: parseInt(e.target.value) }))}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              disabled={isFetching}
            >
              {Array.from({ length: 52 }, (_, i) => i + 1).map(week => (
                <option key={week} value={week}>Week {week}</option>
              ))}
            </select>
            <select
              value={filters.branch}
              onChange={(e) => setFilters(prev => ({ ...prev, branch: e.target.value, rep: '' }))}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              disabled={isFetching}
            >
              <option value="">All Branches</option>
              {branches && branches.map((branch) => (
                <option key={branch._id} value={branch.MamCode}>
                  {branch.MamCode}
                </option>
              ))}
            </select>
            <select
              value={filters.rep}
              onChange={(e) => setFilters(prev => ({ ...prev, rep: e.target.value }))}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              disabled={isFetching}
            >
              <option value="">All Operators</option>
              {operators && operators
                .filter(op => !filters.branch || op.Branch.toLowerCase() === filters.branch.toLowerCase())
                .map((operator) => (
                  <option key={operator.Inits} value={operator.Inits}>
                    {`${operator.Inits} - ${operator.Name}`}
                  </option>
                ))}
            </select>
          </>
        );

      case 'area-analysis':
        return (
          <select
            value={filters.period}
            onChange={(e) => setFilters(prev => ({ ...prev, period: e.target.value }))}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            disabled={isFetching}
          >
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
          </select>
        );

      case 'daily-sales':
        return (
          <div className="flex gap-2 items-center">
            <div className="relative">
              <input
                type="date"
                value={`${filters.startDate.slice(0, 4)}-${filters.startDate.slice(4, 6)}-${filters.startDate.slice(6, 8)}`}
                onChange={(e) => {
                  const selectedDate = new Date(e.target.value);
                  const formattedDate = format(selectedDate, 'yyyyMMdd');
                  setFilters(prev => ({
                    ...prev,
                    startDate: formattedDate
                  }));
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pl-10"
                disabled={isFetching}
              />
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            </div>
            <span className="text-gray-500 text-sm">to</span>
            <div className="relative">
              <input
                type="date"
                value={`${filters.endDate.slice(0, 4)}-${filters.endDate.slice(4, 6)}-${filters.endDate.slice(6, 8)}`}
                onChange={(e) => {
                  const selectedDate = new Date(e.target.value);
                  const formattedDate = format(selectedDate, 'yyyyMMdd');
                  setFilters(prev => ({
                    ...prev,
                    endDate: formattedDate
                  }));
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pl-10"
                disabled={isFetching}
              />
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const renderReport = () => {
    switch (filters.reportType) {
      case 'invoice-summary':
        return <InvoiceSummary data={salesData} isLoading={isLoading} searchTerm={filters.searchTerm} />;
      case 'six-month-sales':
        return <SixMonthSales data={salesData} isLoading={isLoading} searchTerm={filters.searchTerm} />;
      case 'area-analysis':
        return <AreaAnalysis data={salesData} isLoading={isLoading} searchTerm={filters.searchTerm} />;
      case 'weekly-sales':
        return <WeeklySales data={salesData} isLoading={isLoading} searchTerm={filters.searchTerm} />;
      case 'daily-sales':
        return <DailySales data={salesData} isLoading={isLoading} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen">
      {isFetching && (
        <div className="fixed inset-0 bg-black/10 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg shadow-xl flex flex-col items-center gap-3">
            <Loader2 className="h-10 w-10 animate-spin text-blue-500" />
            <span className="text-gray-700 font-medium">Loading report data...</span>
          </div>
        </div>
      )}

      <div className="p-6 mt-8">
        {/* Overall KPI Cards - Show when data is loaded */}
        {salesData && salesData.length > 0 && (
          <div className="mb-6">
            {(() => {
              // Calculate overall totals from all data
              const overallTotals = salesData.reduce((acc, item) => ({
                sales: acc.sales + (item.sales || 0),
                cost: acc.cost + (item.cost || 0),
                marginValue: acc.marginValue + (item.marginValue || 0),
                invoices: acc.invoices + (item.invoices || 0),
                credits: acc.credits + (item.credits || 0)
              }), { sales: 0, cost: 0, marginValue: 0, invoices: 0, credits: 0 });

              const overallMarginPercent = overallTotals.sales > 0 
                ? (overallTotals.marginValue / overallTotals.sales * 100) 
                : 0;

              return (
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
                    <div className="text-sm font-medium text-blue-700 mb-1">Total Sales</div>
                    <div className="text-2xl font-bold text-blue-900">£{overallTotals.sales.toLocaleString('en-GB', { maximumFractionDigits: 0 })}</div>
                    <div className="text-xs text-blue-600 mt-1">All Records</div>
                  </div>
                  <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4 border border-orange-200">
                    <div className="text-sm font-medium text-orange-700 mb-1">Total Cost</div>
                    <div className="text-2xl font-bold text-orange-900">£{overallTotals.cost.toLocaleString('en-GB', { maximumFractionDigits: 0 })}</div>
                    <div className="text-xs text-orange-600 mt-1">All Records</div>
                  </div>
                  <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
                    <div className="text-sm font-medium text-green-700 mb-1">Total Margin</div>
                    <div className="text-2xl font-bold text-green-900">£{overallTotals.marginValue.toLocaleString('en-GB', { maximumFractionDigits: 0 })}</div>
                    <div className="text-xs text-green-600 mt-1">
                      {overallMarginPercent.toFixed(1)}% margin
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
                    <div className="text-sm font-medium text-purple-700 mb-1">Total Invoices</div>
                    <div className="text-2xl font-bold text-purple-900">£{overallTotals.invoices.toLocaleString('en-GB', { maximumFractionDigits: 0 })}</div>
                    <div className="text-xs text-purple-600 mt-1">Sales before credits</div>
                  </div>
                  <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-4 border border-red-200">
                    <div className="text-sm font-medium text-red-700 mb-1">Total Credits</div>
                    <div className="text-2xl font-bold text-red-900">£{overallTotals.credits.toLocaleString('en-GB', { maximumFractionDigits: 0 })}</div>
                    <div className="text-xs text-red-600 mt-1">All Records</div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* Enhanced Filter Panel */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Sales Report Filters</h2>
            <div className="text-sm text-gray-500">
              {salesData && salesData.length > 0 && (
                <span>{salesData.length} records loaded</span>
              )}
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Report Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Report Type</label>
              <select
                value={filters.reportType}
                onChange={(e) => setFilters(prev => ({ ...prev, reportType: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                disabled={isFetching}
              >
                <option value="invoice-summary">Invoice Summary</option>
                <option value="six-month-sales">6M Sales (Area)</option>
                <option value="area-analysis">Area Analysis</option>
                <option value="weekly-sales">Weekly Sales</option>
                <option value="daily-sales">Daily Sales</option>
              </select>
            </div>

            {/* Dynamic Filters */}
            <div className="md:col-span-2 lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Filters</label>
              <div className="flex flex-wrap gap-2">
                {renderFilters()}
                
                {filters.reportType !== 'weekly-sales' && (
                  <select
                    value={filters.branch}
                    onChange={(e) => setFilters(prev => ({ ...prev, branch: e.target.value }))}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    disabled={isFetching}
                  >
                    <option value="">All Branches</option>
                    {branches && branches.map((branch) => (
                      <option key={branch._id} value={branch.MamCode}>
                        {branch.MamCode}
                      </option>
                    ))}
                  </select>
                )}

                {filters.reportType !== 'daily-sales' && filters.reportType !== 'weekly-sales' && (
                  <input
                    type="text"
                    placeholder="Search customers, reps, dates..."
                    value={filters.searchTerm}
                    onChange={(e) => setFilters(prev => ({ ...prev, searchTerm: e.target.value }))}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex-1 min-w-[200px]"
                    disabled={isFetching}
                  />
                )}
              </div>
            </div>

            {/* Action Button */}
            <div className="flex items-end">
              <button
                onClick={handleFetch}
                disabled={isFetching}
                className="w-full px-6 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium shadow-sm transition-colors"
              >
                {isFetching && <Loader2 className="h-4 w-4 animate-spin" />}
                {isFetching ? 'Loading...' : 'Load Report'}
              </button>
            </div>
          </div>
        </div>

        {renderReport()}
      </div>
    </div>
  );
}

export default Sales;